import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject, Subscription } from 'rxjs'
import { debounceTime, finalize, switchMap, takeUntil } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { FormValidationService } from 'src/app/services/form-validation.service'
import { durationText } from 'src/app/shared/utils'
import {
  sanitizeAwsAccountId,
  sanitizeAwsRegion,
  wellFormedAwsAccountId,
  wellFormedAwsRegion,
} from 'src/utils/aws-utils'
import { RegionsService } from 'src/app/services/regions.service'
import { AccountService } from '../../services/account.service'
import { ApplicationCreatorState } from '../../services/application-creator-state.service'
import { ApplicationProviderService } from '../../services/application.service'
import { ApplicationType, IdentityProviderDto } from '../../../services/dtos'
import { wellFormedAzureSubscriptionId } from '../../../../utils/azure-utils'
import { IdpModalComponent, IdpModalData, IdpModalResult } from '../shared/idp-modal/idp-modal.component'
import { BaseError } from '../../../../utils/errors'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'application-creator-details-page',
  templateUrl: './application-creator-details-page.component.html',
  styleUrls: ['./application-creator-details-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ApplicationCreatorDetailsPageComponent implements OnInit, OnDestroy {
  @ViewChild('idpModal', { static: true }) idpModal: IdpModalComponent

  private destroy$ = new Subject<void>()
  private createIdentityProviderSubscription: Subscription = null

  applicationFormModel = this.formBuilder.group(
    {
      applicationName: ['', Validators.required],
      applicationType: ['aws', Validators.required],
      applicationRpo: ['', [Validators.required, Validators.min(0), Validators.max(48 * 60 * 60)]],
      identityProviderId: [''],
      sourceAccountId: ['', [Validators.required]],
      sourceRegion: ['', [Validators.required]],
      targetAccountId: ['', [Validators.required]],
      targetRegion: ['', [Validators.required]],
    },
    {
      validators: [
        this.checkForExistingAppWithSameName.bind(this),
        this.checkForExistingAppWithSameSourceEndpointButDifferentTargetAccount.bind(this),
        this.checkForExistingAppWithDifferentSourceEndpointButSameTargetAccount.bind(this),
      ],
    }
  )

  public entraIdentityProviders: IdentityProviderDto[] = []
  public showBackToWelcome = false
  public azWarningAcknowledged = false

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    private apiService: ApiService,
    private uiService: UiService,
    private formBuilder: UntypedFormBuilder,
    private applicationCreatorState: ApplicationCreatorState,
    private regionsService: RegionsService,
    private formValidationService: FormValidationService
  ) {}

  onSubmit() {
    this.saveFormToState()
    // A fresh pass through the wizard re-asks the outdated-template question, so a
    // choice held over from an earlier pass never silently decides it.
    this.applicationCreatorState.templateChoice = null
    this.applicationCreatorState.saveToLocalStorage()

    // Check if we need to show access method selection (CloudFormation vs StackSets)
    // This only appears for AWS applications when the account has StackSets capability
    // and hasn't yet made a choice (first-time setup)
    const isAws = this.applicationType === ApplicationType.AWS
    const hasStacksetsCapability = this.accountService.stacksetsCapabilityEnabled
    const hasChoice = this.accountService.hasStacksetsChoice

    if (isAws && hasStacksetsCapability && !hasChoice) {
      this.router.navigateByUrl(this.uiService.buildNewApplicationAccessMethodUrl(this.accountService.accountId))
    } else {
      this.router.navigateByUrl(this.uiService.buildNewApplicationConnectionUrl(this.accountService.accountId))
    }
  }

  private saveFormToState(): void {
    this.applicationCreatorState.applicationType = this.applicationType
    this.applicationCreatorState.applicationName = this.applicationName
    this.applicationCreatorState.applicationRpo = this.rpo
    this.applicationCreatorState.sourceAccountId = this.sourceAccountId
    this.applicationCreatorState.sourceRegion = this.sourceRegion
    this.applicationCreatorState.targetAccountId =
      this.targetAccountId.length > 0 ? this.targetAccountId : this.sourceAccountId
    this.applicationCreatorState.targetRegion = this.targetRegion
    this.applicationCreatorState.identityProviderId = this.identityProviderId
  }

  ngOnInit() {
    // Check if user came from welcome page
    this.showBackToWelcome = this.activatedRoute.snapshot.queryParamMap.get('from') === 'welcome'

    const creator = this.applicationCreatorState
    const primary = this.activatedRoute.snapshot.queryParams['primary']
    if (primary?.includes('/')) {
      ;[creator.sourceAccountId, creator.sourceRegion] = primary.split('/')
    }
    const recovery = this.activatedRoute.snapshot.queryParams['recovery']
    if (recovery?.includes('/')) {
      ;[creator.targetAccountId, creator.targetRegion] = recovery.split('/')
    }
    // Store recovery group ID if passed (for adding app to group after creation)
    const recoveryGroupId = this.activatedRoute.snapshot.queryParams['recoveryGroupId']
    if (recoveryGroupId) {
      creator.recoveryGroupId = recoveryGroupId
    }

    this.accountService.idpListStream.pipe(takeUntil(this.destroy$)).subscribe((idps) => {
      this.entraIdentityProviders = idps
        .filter((idp) => idp.type === 'msEntra')
        .sort((a, b) => a.name.localeCompare(b.name))
    })

    this.applicationFormModel.get('applicationType')?.valueChanges.subscribe((applicationType) => {
      const sourceAccountIdControl = this.applicationFormModel.get('sourceAccountId')
      const targetAccountIdControl = this.applicationFormModel.get('targetAccountId')
      const sourceRegionControl = this.applicationFormModel.get('sourceRegion')
      const targetRegionControl = this.applicationFormModel.get('targetRegion')
      const identityProviderControl = this.applicationFormModel.get('identityProviderId')

      if (applicationType === ApplicationType.AWS) {
        sourceAccountIdControl?.setValidators([Validators.required, wellFormedAwsAccountId])
        targetAccountIdControl?.setValidators([wellFormedAwsAccountId])
        sourceRegionControl?.setValidators([Validators.required, wellFormedAwsRegion])
        targetRegionControl?.setValidators([Validators.required, wellFormedAwsRegion])
        identityProviderControl?.setValidators([])
      } else if (applicationType === ApplicationType.AZURE) {
        sourceAccountIdControl?.setValidators([Validators.required, wellFormedAzureSubscriptionId])
        targetAccountIdControl?.setValidators([wellFormedAzureSubscriptionId])
        sourceRegionControl?.setValidators([Validators.required])
        targetRegionControl?.setValidators([Validators.required])
        identityProviderControl?.setValidators([Validators.required])

        if (identityProviderControl?.value === '' && this.entraIdentityProviders.length > 0) {
          identityProviderControl.setValue(this.entraIdentityProviders[0].identityProviderId)
        }
      }

      sourceAccountIdControl?.updateValueAndValidity()
      targetAccountIdControl?.updateValueAndValidity()
      identityProviderControl?.updateValueAndValidity()
    })

    let applicationType: ApplicationType | ''
    if (this.awsEnabled && this.azureEnabled) {
      applicationType = creator.applicationType || ''
    } else if (this.azureEnabled) {
      applicationType = ApplicationType.AZURE
    } else {
      applicationType = ApplicationType.AWS
    }

    this.applicationFormModel.setValue({
      applicationName: creator.applicationName,
      applicationType: applicationType,
      applicationRpo: creator.applicationRpo,
      sourceAccountId: creator.sourceAccountId,
      sourceRegion: creator.sourceRegion,
      targetAccountId: creator.targetAccountId,
      targetRegion: creator.targetRegion,
      identityProviderId: creator.identityProviderId,
    })

    // Reset AZ mismatch acknowledgment when regions change
    this.applicationFormModel
      .get('sourceRegion')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.azWarningAcknowledged = false
      })
    this.applicationFormModel
      .get('targetRegion')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.azWarningAcknowledged = false
      })

    // Set up validation to prevent same-account same-region configurations
    this.formValidationService.setupSameAccountSameRegionValidation(
      this.applicationFormModel,
      sanitizeAwsAccountId,
      sanitizeAwsRegion
    )

    // Auto-save form values to state on changes (debounced)
    this.applicationFormModel.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
      this.saveFormToState()
      this.applicationCreatorState.markDirty()
    })

    this.setNotificationEmail()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private setNotificationEmail() {
    this.apiService
      .getAuthenticatedSubject()
      .pipe(
        switchMap((authSubject) => this.apiService.getUser(authSubject.subjectId)),
        takeUntil(this.destroy$)
      )
      .subscribe((user) => this.applicationCreatorState.applicationNotificationEmails.push(user.email))
  }

  cancelButtonClick() {
    this.router.navigateByUrl(this.uiService.buildAccountRootUrl(this.accountService.accountId))
  }

  navigateBackToWelcome() {
    this.router.navigateByUrl(this.uiService.buildAccountWelcomeUrl(this.accountService.accountId))
  }

  get accountId(): string {
    return this.accountService.accountId
  }

  get awsEnabled() {
    return this.accountService.awsEnabled()
  }

  get azureEnabled() {
    return this.accountService.azureEnabled()
  }

  get showCloudProviderSelector(): boolean {
    return this.awsEnabled && this.azureEnabled
  }

  get baseStepNumber(): number {
    let step = 1
    if (this.showCloudProviderSelector) step++
    if (this.applicationType === ApplicationType.AZURE) step++
    return step
  }

  get applicationType(): ApplicationType | '' {
    return this.applicationFormModel.value.applicationType as ApplicationType | ''
  }

  get sameRegionSyncPairsEntitled() {
    return this.accountService.entitledForCapability('sameRegionSyncPairs')
  }

  get sourceRegion() {
    return this.applicationFormModel.value.sourceRegion.trim()
  }

  private get applicationName() {
    return this.applicationFormModel.value.applicationName as string
  }

  private get rpo() {
    return parseFloat(this.applicationFormModel.value.applicationRpo)
  }

  get enableRealTimeRpo() {
    return this.accountService.entitledForCapability('realTimeRpos')
  }

  get isRealTimeRpo() {
    return this.rpo === 0
  }

  get rpoText() {
    if (this.rpo === 0) {
      return 'Real-time replication'
    }
    return durationText(this.rpo)
  }

  get sourceEndpointUri() {
    return `${this.applicationType}:${this.sourceAccountId}:${this.sourceRegion}`
  }

  get targetEndpointUri() {
    return `${this.applicationType}:${this.targetAccountId}:${this.targetRegion}`
  }

  get sourceAccountId() {
    return this.applicationFormModel.value.sourceAccountId.trim()
  }

  get targetAccountId() {
    return this.applicationFormModel.value.targetAccountId.trim()
  }

  get targetRegion() {
    return this.applicationFormModel.value.targetRegion.trim()
  }

  get showAzMismatchWarning(): boolean {
    return (
      this.applicationType === ApplicationType.AZURE &&
      this.regionsService.azureRegionSupportsAzs(this.sourceRegion) &&
      this.targetRegion !== '' &&
      !this.regionsService.azureRegionSupportsAzs(this.targetRegion)
    )
  }

  get identityProviderId() {
    return this.applicationFormModel.value.identityProviderId
  }

  get sourceAccountPlaceholder() {
    // Show AWS placeholder if Azure is not enabled or if AWS is explicitly selected
    return !this.azureEnabled || this.applicationType === ApplicationType.AWS
      ? 'AWS Account ID (123456789012)'
      : 'Azure Subscription ID (00000000-0000-0000-0000-000000000000)'
  }

  get targetAccountPlaceholder() {
    // Show AWS placeholder if Azure is not enabled or if AWS is explicitly selected
    return !this.azureEnabled || this.applicationType === ApplicationType.AWS
      ? 'AWS Account ID (987654321098)'
      : 'Azure Subscription ID (11111111-1111-1111-1111-111111111111)'
  }

  checkForExistingAppWithSameName(form: AbstractControl): ValidationErrors | null {
    const appName = form.get('applicationName').value

    if (appName) {
      try {
        const apps = this.applicationProviderService.loadedApplications()
        if (apps.some((a) => a.name == appName)) {
          return { existingAppWithSameName: appName }
        }
      } catch (error) {
        console.warn('Unable to validate application name - cache not initialized:', error)
      }
    }

    return null
  }

  checkForExistingAppWithSameSourceEndpointButDifferentTargetAccount(form: AbstractControl): ValidationErrors | null {
    const sourceAccountId = form.get('sourceAccountId').value
    const sourceRegion = form.get('sourceRegion').value
    const targetAccountId = form.get('targetAccountId').value
    const targetRegion = form.get('targetRegion').value

    if (sourceAccountId && sourceRegion && targetAccountId && targetRegion) {
      try {
        const apps = this.applicationProviderService.loadedApplications()
        const appsWithSameSourceAndTargetRegion = apps.filter(
          (a) =>
            a.sourceAwsAccountId == sourceAccountId && a.sourceRegion == sourceRegion && a.targetRegion == targetRegion
        )

        if (
          appsWithSameSourceAndTargetRegion.length > 0 &&
          appsWithSameSourceAndTargetRegion[0].targetAwsAccountId != targetAccountId
        ) {
          return {
            existingAppWithSameSourceButDifferentTargetAccount: appsWithSameSourceAndTargetRegion[0].name,
          }
        }
      } catch (error) {
        console.warn('Unable to validate source endpoint - cache not initialized:', error)
      }
    }

    return null
  }

  checkForExistingAppWithDifferentSourceEndpointButSameTargetAccount(form: AbstractControl): ValidationErrors | null {
    const sourceAccountId = form.get('sourceAccountId').value
    const sourceRegion = form.get('sourceRegion').value
    const targetAccountId = form.get('targetAccountId').value
    const targetRegion = form.get('targetRegion').value

    if (targetAccountId && targetRegion) {
      try {
        const apps = this.applicationProviderService.loadedApplications()
        const duplicate = apps.find(
          (a) =>
            a.targetAwsAccountId === targetAccountId &&
            a.targetRegion === targetRegion &&
            (a.sourceAwsAccountId !== sourceAccountId || a.sourceRegion !== sourceRegion)
        )
        // Arpio staff can enable a capability to allow this for customers with a business
        // need (after proper warning), so we'll consider the form valid in those cases.
        const preventDuplicates = !this.accountService.entitledForCapability('targetAccountReuse')

        if (preventDuplicates && duplicate) {
          form.get('targetAccountId')?.setErrors({
            existingAppWithDifferentSourceButSameTargetAccount: duplicate.name,
          })
          form.get('targetRegion')?.setErrors({
            existingAppWithDifferentSourceButSameTargetAccount: duplicate.name,
          })
          return {
            existingAppWithDifferentSourceButSameTargetAccount: duplicate.name,
          }
        } else {
          // Clear previous errors if validation passes
          form.get('targetAccountId')?.setErrors(null)
          form.get('targetRegion')?.setErrors(null)
        }
      } catch (error) {
        console.warn('Unable to validate target endpoint - cache not initialized:', error)
      }
    }

    return null
  }

  onSourceRegionChanged(newSourceRegion: string) {
    const currentTargetRegion = this.targetRegion

    // Check if the current target region would become invalid
    if (currentTargetRegion && currentTargetRegion === newSourceRegion) {
      // Check if same region is allowed based on entitlement and account differences
      const sourceAccount = this.sourceAccountId
      const targetAccount = this.targetAccountId

      // Same-region sync requires:
      // 1. The sameRegionSyncPairs entitlement to be enabled
      // 2. Cross-account configuration (different source and target accounts)
      // Same-account same-region is never allowed (not a valid configuration)
      const sameRegionAllowed = this.sameRegionSyncPairsEntitled && !!targetAccount && sourceAccount !== targetAccount

      if (!sameRegionAllowed) {
        // Reset target region to empty since it's no longer valid
        this.applicationFormModel.patchValue({ targetRegion: '' })
      }
    }
  }

  get allFormErrors() {
    const errors: ValidationErrors = {}
    Object.keys(this.applicationFormModel.controls).forEach((key) => {
      const controlErrors = this.applicationFormModel.get(key)?.errors
      if (controlErrors) {
        errors[key] = controlErrors
      }
    })
    const keys = Object.keys(errors).length ? errors : null
    return JSON.stringify(keys)
  }

  // TODO: Refactor - This IDP modal logic is duplicated in account-identity-providers.component.ts
  // Consider creating a shared IdpModalService to handle modal operations
  openAddEntraIdpDialog() {
    const modalData: IdpModalData = {
      type: 'msEntra',
      operation: 'create',
    }
    this.idpModal.open(modalData)
  }

  onIdpModalSave(data: IdpModalResult) {
    this.idpModal.loading = true

    this.createIdentityProviderSubscription = this.accountService
      .createMsEntraIdentityProvider$(data.name, data.tenantId, data.useForAuthentication, data.autoCreateSubjects)
      .pipe(finalize(() => (this.createIdentityProviderSubscription = null)))
      .subscribe(
        (idp: IdentityProviderDto) => {
          // Add to account service and emit to stream so dropdown updates
          this.accountService.addIdentityProvider(idp)
          // Auto-select the newly created identity provider
          this.applicationFormModel.patchValue({ identityProviderId: idp.identityProviderId })
          this.idpModal.close()
        },
        (error: BaseError) => {
          this.idpModal.loading = false
          this.uiService.announceApiError(error, 'Failed to create identity provider')
        }
      )
  }

  onIdpModalCancel() {
    // Modal handles its own closing
  }
}
